import { Trans, useTranslation } from 'react-i18next';

import {
    Accordion,
    Anchor,
    Body1,
    List,
    ListItem,
    Stack,
    Subtitle2,
} from '@myphx-engineering/phx-design-system';

import externalRoutes from '../../../../external-routes';
import { formatToLocalizedDate } from '../../../../util';
import { ConditionalComponent } from '../../../utility/ConditionalComponent';
import { DecisionLetterLink } from '../DecisionLetterLink';

export const DeniedDescription = ({
    trackingNumber,
    appealBeforeDate,
    rationale,
    isPrescriberOrder,
    isSpecialty,
    paId,
}: {
    paId: string;
    trackingNumber?: string | null;
    appealBeforeDate?: string | null;
    rationale?: string | null;
    isPrescriberOrder?: boolean;
    isSpecialty?: boolean;
}) => {
    const { t } = useTranslation();

    return (
        <Stack gap="sm">
            <Stack gap="xxs">
                <Body1>
                    {t('priorAuthorization.statusPage.denied.description')}
                </Body1>
                {rationale ? (
                    <Stack gap="xxxs">
                        <Subtitle2>
                            {t(
                                'priorAuthorization.statusPage.denied.rationale'
                            )}
                        </Subtitle2>
                        <Body1>{rationale}</Body1>
                    </Stack>
                ) : null}
                <Accordion
                    items={[
                        {
                            header: t(
                                'priorAuthorization.statusPage.denied.otherOptions'
                            ),
                            key: 'denied-other-options',
                            children: (
                                <Stack>
                                    <Stack gap="sm">
                                        <List type="ordered">
                                            <ListItem>
                                                {t(
                                                    'priorAuthorization.statusPage.denied.stepOne'
                                                )}
                                            </ListItem>
                                            {appealBeforeDate ? (
                                                <ListItem>
                                                    <Trans
                                                        i18nKey={
                                                            'priorAuthorization.statusPage.denied.stepTwo'
                                                        }
                                                        components={{
                                                            1: (
                                                                <Anchor
                                                                    target="_blank"
                                                                    href={
                                                                        externalRoutes.appealRequestForm
                                                                    }
                                                                />
                                                            ),
                                                        }}
                                                        values={{
                                                            date: formatToLocalizedDate(
                                                                new Date(
                                                                    appealBeforeDate
                                                                ),
                                                                {
                                                                    dateStyle:
                                                                        'medium',
                                                                }
                                                            ),
                                                        }}
                                                    />
                                                </ListItem>
                                            ) : null}
                                            <ConditionalComponent
                                                condition={
                                                    !(
                                                        isPrescriberOrder &&
                                                        !isSpecialty
                                                    )
                                                }
                                            >
                                                <ListItem>
                                                    {t(
                                                        'priorAuthorization.statusPage.denied.stepThree'
                                                    )}
                                                </ListItem>
                                            </ConditionalComponent>
                                        </List>
                                        <Stack gap={0}>
                                            <ConditionalComponent
                                                condition={!!trackingNumber}
                                            >
                                                <Subtitle2>
                                                    {t(
                                                        'priorAuthorization.statusPage.denied.tracking',
                                                        { trackingNumber }
                                                    )}
                                                </Subtitle2>
                                            </ConditionalComponent>
                                            <Subtitle2>
                                                {t(
                                                    'priorAuthorization.statusPage.denied.fax'
                                                )}
                                            </Subtitle2>
                                        </Stack>
                                    </Stack>
                                </Stack>
                            ),
                        },
                    ]}
                />
                <Stack gap="lg">
                    <DecisionLetterLink paId={paId} />
                    <Body1>{t('priorAuth.decisionInfo.footer')}</Body1>
                </Stack>
            </Stack>
        </Stack>
    );
};
